import { AlertTriangle } from "lucide-react";
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

/**
 * The modal a failed write goes into.
 *
 * It exists because the inline banner above the wizard's footer was missable in
 * exactly the case that mattered: the footer sits below the panel, so on any
 * step long enough to scroll — Milestones with six phases, Review with all four
 * sections — a failed Continue rendered its explanation off-screen. The button
 * simply stopped working, and the reason was somewhere below the fold.
 *
 * A dialog is the right shape for *noticing*: the user's next action has to be
 * to deal with it, and every path onward (retry, edit the field, give up)
 * begins with reading what went wrong.
 *
 * It is emphatically **not** a replacement for the banner, and `WizardFooter`
 * renders both — see the note there. A dialog is dismissible, so on any screen
 * that pairs an error with a disabled control, the dialog alone leaves a dead
 * button and no reason once it is closed.
 */
export function ErrorDialog({
  error,
  title = "That didn’t save",
  onDismiss,
}: {
  /** The message, or null when there is nothing wrong. */
  error: string | null;
  title?: string;
  /**
   * Called when the user closes it. Optional: react-query keeps `error` set
   * until the next attempt, so without a way to clear the source this component
   * tracks dismissal itself — see `dismissed` below.
   */
  onDismiss?: () => void;
}) {
  /**
   * What the user has already read and closed.
   *
   * The message, not a boolean. A mutation's `error` persists after dismissal,
   * so a boolean would have to be reset by someone — and whoever forgot would
   * suppress the *next* failure instead. Keying on the text means a second
   * failure with a different message opens the dialog again on its own, and a
   * second identical failure correctly stays quiet.
   */
  const [dismissed, setDismissed] = useState<string | null>(null);

  // A retry that fails differently is a new thing to say. Clearing here rather
  // than in the close handler keeps the two states from disagreeing.
  useEffect(() => {
    if (error && error !== dismissed) setDismissed(null);
  }, [error, dismissed]);

  const open = Boolean(error) && error !== dismissed;

  const close = (): void => {
    setDismissed(error);
    onDismiss?.();
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !next && close()}>
      <DialogContent className="max-w-[460px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2.5">
            <span className="flex shrink-0 text-danger">
              <AlertTriangle size={20} aria-hidden />
            </span>
            {title}
          </DialogTitle>
        </DialogHeader>
        <DialogBody>
          {/* `role="alert"` so it is announced on open rather than only being
              visible — the dialog takes focus, but the message is not what
              receives it. */}
          <DialogDescription role="alert" className="whitespace-pre-line">
            {error}
          </DialogDescription>
        </DialogBody>
        <DialogFooter>
          <Button size="footer" onClick={close}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
